import React from 'react';
import { ClientData, ContentTopic, HeroContent, InsightCardContent, InsightIndexEntry } from '../types';
import InsightIndexList from './charts/InsightIndexList';
import AeoGeoSeoRenderer from './charts/AeoGeoSeoRenderer';
import useReveal from '../hooks/useReveal';

interface ClientSeoPageProps {
  clientData: ClientData;
}

const findAeoSection = (clientData: ClientData) => {
  const sections = clientData.flexibleSections || [];
  return sections.find(
    (section) => {
      const title = (section?.title || '').toLowerCase();
      return title.includes('aeo/geo/seo') || (title.includes('aeo') && title.includes('seo'));
    }
  );
};

const HeroSection: React.FC<{ hero?: HeroContent; fallbackTitle: string; fallbackSubtitle?: string; fallbackColor: string }> = ({
  hero,
  fallbackTitle,
  fallbackSubtitle,
  fallbackColor,
}) => {
  const style = hero?.backgroundImage
    ? {
        backgroundImage: `linear-gradient(120deg, rgba(15,23,42,0.75), rgba(30,41,59,0.65)), url(${hero.backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }
    : {
        background: `linear-gradient(135deg, ${fallbackColor}, rgba(14,116,144,0.95))`,
      };
  return (
    <section className="relative overflow-hidden">
      <div className="min-h-[24vh] md:min-h-[28vh] flex items-center py-5" style={style}>
        <div className="container mx-auto px-6">
          {hero?.eyebrow && (
            <div className="text-xs uppercase tracking-[0.35em] text-white/70 mb-3">
              {hero.eyebrow}
            </div>
          )}
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
            {hero?.title || fallbackTitle}
          </h1>
          <p className="text-white/85 text-base md:text-lg max-w-3xl leading-relaxed">
            {hero?.subtitle || fallbackSubtitle}
          </p>
          {hero?.description && (
            <p className="text-white/75 text-sm md:text-base mt-4 max-w-3xl leading-relaxed">
              {hero.description}
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

const IntroCard: React.FC<{ intro?: InsightCardContent; themeColor: string }> = ({ intro, themeColor }) => {
  if (!intro?.title && !intro?.body) return null;
  return (
    <div className="bg-white rounded-xl shadow-md p-6 border border-slate-100">
      {intro?.subtitle && (
        <div className="text-xs uppercase tracking-[0.3em] text-gray-500 mb-3">
          {intro.subtitle}
        </div>
      )}
      {intro?.title && (
        <h2 className="text-2xl font-bold text-slate-900 mb-4">{intro.title}</h2>
      )}
      {intro?.body && <p className="text-gray-700 leading-relaxed mb-4">{intro.body}</p>}
      {intro?.bulletPoints && intro.bulletPoints.length > 0 && (
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-700">
          {intro.bulletPoints.map((item, idx) => (
            <li key={idx} className="flex items-start">
              <span className="mt-1 mr-2 h-1.5 w-1.5 rounded-full" style={{ backgroundColor: themeColor }} />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      )}
      {intro?.ctaLabel && intro?.ctaUrl && (
        <a
          href={intro.ctaUrl}
          className="inline-flex text-sm font-semibold items-center mt-4"
          style={{ color: themeColor }}
        >
          {intro.ctaLabel}
          <svg className="ml-2 h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7" />
          </svg>
        </a>
      )}
    </div>
  );
};

const ContentTopicsSection: React.FC<{ topics?: ContentTopic[]; themeColor: string }> = ({ topics, themeColor }) => {
  if (!topics || topics.length === 0) return null;
  const { ref: topicsRef, isVisible: topicsVisible } = useReveal<HTMLDivElement>({ threshold: 0.2, once: true });
  return (
    <section className="mt-12" ref={topicsRef}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-3xl font-bold text-slate-900">Content Topics</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {topics.map((topic, idx) => (
          <div
            key={topic.id || idx}
            className={`bg-white rounded-xl shadow-md border border-slate-100 p-6 transition-all duration-700 ${
              topicsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
            style={{ transitionDelay: topicsVisible ? `${idx * 120}ms` : '0ms' }}
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs uppercase tracking-[0.3em] text-gray-400">{idx === 0 ? 'Sample Topics' : `Topic ${idx + 1}`}</span>
              <span className="text-xs font-semibold px-3 py-1 rounded-full" style={{ backgroundColor: themeColor, color: '#fff' }}>
                Priority
              </span>
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-3">{topic.title || `Topic ${idx + 1}`}</h3>
            {topic.description && (
              <p className="text-sm text-slate-600 leading-relaxed mb-3">{topic.description}</p>
            )}
            {topic.bullets && topic.bullets.length > 0 && (
              <ul className="list-disc list-inside text-sm text-slate-600 space-y-1">
                {topic.bullets.map((point, pointIdx) => (
                  <li
                    key={pointIdx}
                    className={`transition-all duration-500 ${
                      topicsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
                    }`}
                    style={{ transitionDelay: topicsVisible ? `${150 + pointIdx * 60}ms` : '0ms' }}
                  >
                    {point}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

export default function ClientSeoPage({ clientData }: ClientSeoPageProps) {
  const themeColor = clientData.uiSettings?.theme?.primary || '#14B8A6';
  const seoContent = clientData.pageContent?.seo;
  const hero = seoContent?.hero;
  const aeoSection = findAeoSection(clientData);
  const hasAeoData = Boolean(
    aeoSection &&
    aeoSection.data &&
    (Array.isArray(aeoSection.data?.points) ||
      Array.isArray(aeoSection.data) ||
      Array.isArray(aeoSection.data?.currentState?.points))
  );
  const legacyFlags = clientData.uiSettings?.legacyCharts || {};
  const shouldShowLegacy = (flag: any, hasData: boolean) => (flag ?? true) && hasData;

  const flexibleSections = clientData.flexibleSections || [];
  const skipSectionTitles = new Set<string>();

  const normalizeTitle = (title: string) => String(title || '').toLowerCase().trim();
  const gatherInsightCandidates = (node: any, visited = new Set<any>()): any[] => {
    if (!node || visited.has(node)) return [];
    visited.add(node);
    const data = node?.data ?? node;
    if (Array.isArray(data)) return data;
    if (!data || typeof data !== 'object') return [];

    let result: any[] = [];
    const candidateKeys = ['values', 'items', 'insights', 'data', 'rows', 'entries', 'cards', 'sections', 'series', 'list'];
    candidateKeys.forEach(key => {
      if (key in data) result = result.concat(gatherInsightCandidates(data[key], visited));
    });
    Object.values(data).forEach((value: any) => {
      if (Array.isArray(value)) result = result.concat(value);
      else if (value && typeof value === 'object') result = result.concat(gatherInsightCandidates(value, visited));
    });
    return result;
  };

  const normalizeInsightRows = (items: any[]): InsightIndexEntry[] => {
    const seen = new Set<string>();
    const flatten = (item: any): any[] => {
      if (!item) return [];
      if (Array.isArray(item)) return item.flatMap(flatten);
      if (Array.isArray(item.values)) return item.values.flatMap(flatten);
      if (Array.isArray(item.items)) return item.items.flatMap(flatten);
      if (Array.isArray(item.insights)) return item.insights.flatMap(flatten);
      if (Array.isArray(item.data)) return item.data.flatMap(flatten);
      return [item];
    };

    return items
      .flatMap(flatten)
      .map(raw => {
        if (!raw) return null;
        const insight = raw.insight || raw.label || raw.name;
        const index =
          typeof raw.index === 'number'
            ? raw.index
            : typeof raw.value === 'number'
              ? raw.value
              : typeof raw.score === 'number'
                ? raw.score
                : undefined;
        const composition =
          typeof raw.composition === 'number'
            ? raw.composition
            : typeof raw.compositionPercent === 'number'
              ? raw.compositionPercent
              : typeof raw['%'] === 'number'
                ? raw['%']
                : undefined;
        if (!insight || typeof index !== 'number') return null;
        const key = `${insight}-${index}-${composition ?? ''}`;
        if (seen.has(key)) return null;
        seen.add(key);
        return { insight, index, composition } as InsightIndexEntry;
      })
      .filter(Boolean) as InsightIndexEntry[];
  };

  const findSection = (matcher: string) => flexibleSections.find(sec => normalizeTitle(sec?.title || '').includes(matcher));

  const deriveInsightData = (primary: InsightIndexEntry[] | undefined, matcher: string) => {
    if (primary && primary.length > 0) return primary;
    const section = findSection(matcher);
    if (!section) return [];
    skipSectionTitles.add(normalizeTitle(section.title));
    return normalizeInsightRows(gatherInsightCandidates(section));
  };

  const psychologicalSection = findSection('psychological');
  const hobbiesSection = findSection('hobbies');
  const routineSection = findSection('daily routine');

  const psychologicalDriversData = deriveInsightData(clientData.psychologicalDrivers, 'psychological');
  const hobbiesHabitsData = deriveInsightData(clientData.hobbiesAndHabits, 'hobbies');
  const dailyRoutineData = deriveInsightData(clientData.dailyRoutine, 'daily routine');

  const showPsychologicalDrivers = shouldShowLegacy(
    legacyFlags.psychologicalDrivers !== false,
    psychologicalDriversData.length > 0 || !!psychologicalSection
  );
  const showHobbiesHabits = shouldShowLegacy(
    legacyFlags.hobbiesAndHabits !== false,
    hobbiesHabitsData.length > 0 || !!hobbiesSection
  );
  const showDailyRoutine = shouldShowLegacy(
    legacyFlags.dailyRoutine !== false,
    dailyRoutineData.length > 0 || !!routineSection
  );

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      <HeroSection
        hero={hero}
        fallbackTitle="AI SEO (AEO/GEO/SEO) Insights"
        fallbackSubtitle="Visualize competitive share-of-voice alongside sentiment, and identify the most impactful opportunities."
        fallbackColor={themeColor}
      />

      <div className="container mx-auto px-6 py-12 flex-1">
        {seoContent?.intro && (seoContent.intro.title || seoContent.intro.body) ? (
          <div className="bg-white rounded-xl shadow-md p-6 border border-slate-100 mb-10">
            {seoContent?.intro?.subtitle && (
              <div className="text-xs uppercase tracking-[0.3em] text-gray-500 mb-3">
                {seoContent.intro.subtitle}
              </div>
            )}
            {seoContent?.intro?.title && (
              <h2 className="text-2xl font-bold text-slate-900 mb-4">{seoContent.intro.title}</h2>
            )}
            {seoContent?.intro?.body && (
              <div
                className="prose prose-slate max-w-none"
                dangerouslySetInnerHTML={{ __html: seoContent.intro.body }}
              />
            )}
            {(seoContent?.intro?.ctaLabel && seoContent?.intro?.ctaUrl) && (
              <a
                href={seoContent.intro.ctaUrl}
                className="inline-flex text-sm font-semibold items-center mt-4"
                style={{ color: themeColor }}
              >
                {seoContent.intro.ctaLabel}
                <svg className="ml-2 h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7" />
                </svg>
              </a>
            )}
          </div>
        ) : null}

        <section className="mt-10">
          {hasAeoData ? (
            <AeoGeoSeoRenderer
              title={aeoSection?.title || 'AEO/GEO/SEO'}
              data={aeoSection?.data}
              primaryColor={clientData.uiSettings?.theme?.primary}
              secondaryColor={clientData.uiSettings?.theme?.secondary}
            />
          ) : (
            <div className="bg-white rounded-xl shadow-md border border-dashed border-slate-300 p-10 text-center text-slate-500">
              Upload an AEO/GEO/SEO data file in Admin to populate this section.
            </div>
          )}
        </section>

        {/* Lifestyle charts removed per request for SEO page */}

        <ContentTopicsSection topics={seoContent?.contentTopics} themeColor={themeColor} />

        {seoContent?.notes && (
          <section className="mt-12">
            <div className="bg-white rounded-xl shadow-md p-6 border border-slate-100">
              <h2 className="text-2xl font-semibold text-slate-900 mb-3">Additional Notes</h2>
              <p className="text-slate-600 leading-relaxed whitespace-pre-line">{seoContent.notes}</p>
            </div>
          </section>
        )}
      </div>
      <footer className="bg-slate-900/80 border-t border-white/10">
        <div className="container mx-auto px-6 py-12 text-center text-white/70 text-sm">
          <img
            src="https://lhijeqvswxugvtfwkoyl.supabase.co/storage/v1/object/public/client-files/littlefield-logo.png"
            alt="Littlefield Agency"
            className="w-[200px] h-auto object-contain mx-auto mb-2"
          />
          <p>Powered by Littlefield Agency | Customer insights for {clientData.name}</p>
        </div>
      </footer>
    </div>
  );
}
